import React, { useCallback, useMemo, memo } from "react";
import i18next from "i18next";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { withRouter } from "react-router-dom";
import ImageUploading from "react-images-uploading";
import { sortableContainer, sortableElement } from "react-sortable-hoc";

import {
  MEDIA_TYPES,
  FILE_EXTENSIONS,
  globalSystemConstants,
  ALLOWED_PORTFOLIO_FORMATS,
  shutterbDashboardConstants,
} from "../../../../../constants";
import { customToast, convertHeicToJpeg } from "../../../../../shared/utility";

// Memoized SortableImage with checkbox state and transition
const SortableImage = memo(
  sortableElement(
    ({ index, id, image, handleSelect, selectedFiles, handleOpenModal }) => {
      const isChecked = selectedFiles.includes(image);
      const isVideo = image?.file?.type?.startsWith(MEDIA_TYPES.VIDEO);
      const isUsedVideoElement = isVideo && !image?.thumbnailUrl;

      return (
        <div className={`portfolio-item ${isChecked ? "selected" : ""}`}>
          {isVideo && (
            <svg
              width="37"
              height="37"
              viewBox="0 0 37 37"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="play-icon"
              onClick={() => handleOpenModal(id)}
            >
              <circle
                opacity="0.5"
                cx="18.5"
                cy="18.5"
                r="18.5"
                fill="#5FB4BE"
              />
              <g filter="url(#filter0_d_1586_5162)">
                <path
                  d="M26.5981 17.1906C27.2212 17.5504 27.2212 18.4496 26.5981 18.8094L14.4019 25.8509C13.7788 26.2106 13 25.761 13 25.0415L13 10.9585C13 10.239 13.7788 9.78939 14.4019 10.1491L26.5981 17.1906Z"
                  fill="white"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_1586_5162"
                  x="9"
                  y="10.0234"
                  width="22.0645"
                  height="23.9531"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_1586_5162"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect1_dropShadow_1586_5162"
                    result="shape"
                  />
                </filter>
              </defs>
            </svg>
          )}
          <label className="checkbox-wrapper">
            <input
              type="checkbox"
              onChange={() => handleSelect(image)}
              checked={isChecked}
            />
            <span className="checkbox-mark"></span>
          </label>
          <div className="file-wrapper">
            {isUsedVideoElement ? (
              <video
                src={image.url}
                className="file-content"
                style={{ cursor: "grab" }}
                onClick={() => handleOpenModal(id)}
              />
            ) : (
              <img
                alt=""
                src={image?.thumbnailUrl || image.url}
                className="file-content"
                style={{ cursor: "grab" }}
                onClick={() => handleOpenModal(id)}
              />
            )}
          </div>
        </div>
      );
    }
  )
);

// SortableImageList with optimized props
const SortableImageList = sortableContainer(
  ({
    images,
    handleSelect,
    onImageUpload,
    dragProps,
    selectedFiles,
    handleOpenModal,
  }) => {
    return (
      <div className="portfolio-grid overflow-hidden">
        <div
          {...dragProps}
          onClick={onImageUpload}
          className="portfolio-item portfolio-item-uploader"
        >
          <svg
            width="109"
            height="108"
            viewBox="0 0 109 108"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_d_1584_5977)">
              <rect
                x="4.21875"
                y="4"
                width="100"
                height="100"
                rx="50"
                fill="#3E8993"
              />
            </g>
            <path
              d="M67.9688 45.7344H62.6953L59.5391 42.5469C59.5391 42.5469 59.5234 42.5312 59.5156 42.5312L59.5 42.5156C59.0312 42.0469 58.3984 41.75 57.6797 41.75H51.1172C50.3516 41.75 49.6719 42.0781 49.1953 42.6016V42.6094L46.1094 45.7344H40.7188C39.2656 45.7344 38.0938 46.875 38.0938 48.3281V64.1172C38.0938 65.5703 39.2656 66.75 40.7188 66.75H67.9688C69.4141 66.75 70.5938 65.5703 70.5938 64.1172V48.3281C70.5938 46.875 69.4141 45.7344 67.9688 45.7344ZM54.3438 62.8047C50.3672 62.8047 47.125 59.5547 47.125 55.5703C47.125 51.5781 50.3672 48.3359 54.3438 48.3359C58.3281 48.3359 61.5625 51.5781 61.5625 55.5703C61.5625 59.5547 58.3281 62.8047 54.3438 62.8047ZM67.4766 49.9141C66.875 49.9141 66.3828 49.4219 66.3828 48.8125C66.3828 48.2031 66.875 47.7109 67.4766 47.7109C68.0781 47.7109 68.5703 48.2031 68.5703 48.8125C68.5703 49.4219 68.0781 49.9141 67.4766 49.9141Z"
              fill="white"
            />
            <path
              d="M54.3438 50.1035C51.3281 50.1035 48.8906 52.5488 48.8906 55.5723C48.8906 58.5879 51.3281 61.041 54.3438 61.041C57.3516 61.041 59.7969 58.5957 59.7969 55.5723C59.7969 52.5488 57.3516 50.1035 54.3438 50.1035Z"
              fill="white"
            />
            <defs>
              <filter
                id="filter0_d_1584_5977"
                x="0.21875"
                y="0"
                width="108"
                height="108"
                filterUnits="userSpaceOnUse"
                color-interpolation-filters="sRGB"
              >
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="2" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_1584_5977"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_1584_5977"
                  result="shape"
                />
              </filter>
            </defs>
          </svg>
        </div>
        {images.map((image, index) => (
          <SortableImage
            key={image.id || index} // Use unique id if available
            index={index}
            id={index}
            image={image}
            selectedFiles={selectedFiles}
            handleSelect={handleSelect}
            handleOpenModal={handleOpenModal}
          />
        ))}
      </div>
    );
  }
);

const MyPortfolioGallery = ({
  images,
  setImages,
  selectedFiles,
  setSelectedFiles,
  activateSpinner,
  diActivateSpinner,
  handleOpenModal,
}) => {
  const maxNumber = 30;

  // Memoized array move function
  const arrayMove = useCallback((arr, fromIndex, toIndex) => {
    const newArr = [...arr];
    const [element] = newArr.splice(fromIndex, 1);
    newArr.splice(toIndex, 0, element);
    return newArr;
  }, []);

  // Memoized sort handler
  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      setImages((prevImages) => arrayMove(prevImages, oldIndex, newIndex));
    },
    [arrayMove, setImages]
  );

  // Memoized select handler
  const handleSelect = useCallback(
    (image) => {
      setSelectedFiles((prev) => {
        const newFile = prev.includes(image)
          ? prev.filter((file) => file !== image)
          : [...prev, image];
        return newFile;
      });
    },
    [setSelectedFiles]
  );

  // Memoized change handler for image uploads
  const onChange = useCallback(
    async (imageList) => {
      if (
        imageList.length <=
        shutterbDashboardConstants.SB_PORTFOLIO_MAX_IMAGE_COUNT
      ) {
        try {
          activateSpinner();
          const convertedImages = await Promise.all(
            imageList.map(async (image) => {
              const fileExtension = image?.file?.name
                ?.split(".")
                .pop()
                ?.toLowerCase();
              if (
                fileExtension === FILE_EXTENSIONS.HEIC ||
                fileExtension === FILE_EXTENSIONS.HEIF
              ) {
                const convertedFile = await convertHeicToJpeg(image.file);
                return {
                  ...image,
                  file: convertedFile,
                  url: URL.createObjectURL(convertedFile),
                };
              }
              return image;
            })
          );
          setImages(convertedImages);
        } catch (error) {
          customToast(i18next.t("image_conversion_error"), toast.TYPE.ERROR);
        } finally {
          diActivateSpinner();
        }
      } else {
        customToast(i18next.t("max_img_error"), toast.TYPE.ERROR);
      }
    },
    [setImages, activateSpinner, diActivateSpinner]
  );

  // Memoized error handler
  const handleErrorUploading = useCallback(() => {
    customToast(i18next.t("invalid_image_type"), toast.TYPE.ERROR);
  }, []);

  // Memoized sortable props
  const sortableProps = useMemo(
    () => ({
      axis: "xy",
      distance: 5,
      onSortEnd,
      handleSelect,
      handleOpenModal,
    }),
    [onSortEnd, handleSelect, handleOpenModal]
  );

  return (
    <ImageUploading
      multiple
      allowNonImageType
      value={images}
      onError={handleErrorUploading}
      onChange={onChange}
      maxNumber={maxNumber}
      dataURLKey="url"
      acceptType={ALLOWED_PORTFOLIO_FORMATS}
    >
      {({ imageList, onImageUpload, dragProps }) => (
        <div className="upload__image-wrapper">
          <SortableImageList
            images={imageList}
            onImageUpload={onImageUpload}
            dragProps={dragProps}
            {...sortableProps}
            selectedFiles={selectedFiles}
          />
        </div>
      )}
    </ImageUploading>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  sbUserId: state.shutterbAuthentication.sbUserId,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  activateSpinner: () =>
    dispatch({ type: globalSystemConstants.SPINNER_ACTIVATE, data: null }),
  diActivateSpinner: () =>
    dispatch({ type: globalSystemConstants.SPINNER_DIACTIVATE, data: null }),
});

// Mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyPortfolioGallery));
